'use client'

import * as React from 'react'
import { useRouter } from 'next/navigation'
import {
  ArrowUp,
  Bell,
  BellRing,
  Bookmark,
  Eye,
  Heart,
  Layers,
  LockOpen,
  ReceiptText,
  Shield,
  ShieldCheck,
  Sparkles,
  Wallet,
  X,
} from 'lucide-react'
import { useAuth } from '@/components/auth'
import { ListingCard, effectiveListingPrice } from '@/components/listings'
import type { ListingCardStats } from '@/components/listings/listing-card'
import { medianOf } from '@/components/roostr'
import { EXAMPLE_QUERIES } from './home-search-entry'
import { LandingHero } from './landing-hero'
import { useTypewriter } from '@/lib/useTypewriter'
import { useI18n } from '@/lib/i18n'
import { REQUIRE_AUTH_FOR_SEARCH } from '@/lib/feature-flags'
import type { Listing } from '@/lib/types'

// WelcomeGate (§25.3) — web port of mobile-2/app/welcome-gate.tsx (the mobile
// logged-out Home, Phase 24). Shown on `/` to signed-out visitors. It SELLS
// before it asks — a lead hook on instant alerts, a peek-then-wall of REAL
// Czech listings, a feature story and an honest "why an account" — then offers
// Google + email. LANDING-ONLY on web: guest /search stays reachable (unlike
// the mobile hard gate).
//
// Web-native upgrades over the RN port (all faithful to the original web
// prototype's intent): real CSS mask for the wall fade, real backdrop-blur for
// the glass pill, Google sign-in popup that actually works.
// Deviations (flagged in .agent/plan.md §25): "Unlock N more" opens the
// AuthDialog (mobile scrolled to top); sticky bottom Google bar on small
// viewports only; site Header/Footer chrome stays around the gate;
// illustrated hero replaces the live-photo collage (user-approved
// 2026-07-08 — teammate feedback on uncurated photo quality).

export interface GateData {
  total: number | null
  listings: Listing[]
}

export function WelcomeGate({ total, listings }: GateData) {
  const { t, uiLanguage } = useI18n()
  const { signIn } = useAuth()
  const [googleBusy, setGoogleBusy] = React.useState(false)

  const groupCount = (n: number) => n.toLocaleString(uiLanguage === 'cs' ? 'cs-CZ' : 'en-US')

  const withImages = listings.filter((l) => Boolean(l.images?.[0]?.url))
  // Hero is illustrated (no photos), so the teaser takes the three newest.
  const teaser = withImages.slice(0, 3)
  const moreCount = total != null ? Math.max(0, total - 2) : null

  // Same stats recipe as the search grid (listings-grid.tsx) so the teaser
  // renders EXACTLY the card the search page does — same StatRow vizzes and
  // PricePositionBar, pooled over the three teased listings.
  const teaserStats: ListingCardStats = React.useMemo(() => {
    const prices = teaser
      .map((r) => effectiveListingPrice(r))
      .filter((n) => Number.isFinite(n) && n > 0)
    const areas = teaser
      .map((r) => r.area)
      .filter((n): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0)
    const floors = teaser
      .map((r) => r.floor)
      .filter((n): n is number => typeof n === 'number' && Number.isFinite(n))
    const hasPrices = prices.length >= 2
    return {
      prices,
      areas,
      floors,
      priceMin: hasPrices ? Math.min(...prices) : 0,
      priceMax: hasPrices ? Math.max(...prices) : 0,
      priceMedian: hasPrices ? medianOf(prices) : 0,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listings])

  const onGoogle = async () => {
    setGoogleBusy(true)
    try {
      const { signInWithGoogle } = await import('@/lib/firebase')
      await signInWithGoogle()
      // Success → AuthProvider's onAuthChange flips the landing to SearchHome.
    } catch {
      // Cancelled/failed popups are non-fatal on the gate; the AuthDialog
      // remains the fallback path with visible error states.
    } finally {
      setGoogleBusy(false)
    }
  }

  return (
    <div className="relative bg-gate-bg pb-28 sm:pb-10">
      {/* ── HERO ─────────────────────────────────────────────────── */}
      {/* Shared with the signed-in SearchHome (extracted 2026-07-08):
          login must not change the search experience. */}
      <LandingHero />

      {/* Content column — phone-width, widening to a centered desktop column
          (max-w-1120), matching the hero above. */}
      <div className="mx-auto w-full max-w-[680px] lg:max-w-[1120px]">
        {/* ── PEEK-THEN-WALL ───────────────────────────────────────── */}
        {/* The account CTAs used to sit above this section, directly under the
            hero — asking before the page had shown anything, and duplicating
            the header's persistent sign-in. They now close the "honest why"
            card below, where the argument for an account has just been made
            (2026-07-26, user-directed). The wall's unlock pill + the mobile
            sticky bar keep the louder mid-page conversion paths. */}
        <div className="px-[22px] pb-2 pt-10 lg:pb-4 lg:pt-14">
          <SectionEyebrow icon={Eye} text={t('gate.peek.eyebrow', 'A look inside')} />
          <h2 className="mb-1 mt-1.5 text-[24px] font-semibold leading-8 text-gate-text lg:text-[32px] lg:leading-10">
            {total != null ? groupCount(total) : '…'}
            {t('gate.peek.homes', ' live homes, ')}
            <span className="font-serif italic font-normal text-gate-primary">
              {t('gate.peek.one', 'one')}
            </span>
            {t('gate.peek.yours', ' is yours.')}
          </h2>
          <p className="text-[14px] text-gate-text-secondary">
            {t('gate.peek.sub', 'Fresh today across Czechia. Sign in to open the full feed.')}
          </p>
        </div>

        {/* Two REAL search cards (the exact ListingCard the search grid
            renders — photo carousel, price breakdown popover, stat band; its
            own interactions open the public listing detail), then a third
            behind a fade as the wall. */}
        {teaser.length > 0 ? (
          <div className="flex flex-col gap-3 px-[22px] lg:grid lg:grid-cols-3 lg:items-start lg:gap-6">
            {teaser[0] ? <ListingCard listing={teaser[0]} stats={teaserStats} /> : null}
            {teaser[1] ? <ListingCard listing={teaser[1]} stats={teaserStats} /> : null}
            {teaser[2] ? (
              <div className="relative">
                {/* Real CSS mask (the RN port had to approximate with a gradient
                    overlay). Stacked: a short 150px sliver. Desktop grid: the
                    full card fading out from ~40% down. */}
                <div className="max-h-[150px] overflow-hidden [mask-image:linear-gradient(to_bottom,black_10%,transparent_95%)] lg:max-h-none lg:[mask-image:linear-gradient(to_bottom,black_40%,transparent_92%)]">
                  <ListingCard listing={teaser[2]} stats={teaserStats} />
                </div>
                <button
                  onClick={() => signIn('sign-up')}
                  className="absolute bottom-1.5 left-1/2 flex h-11 w-[220px] -translate-x-1/2 items-center justify-center gap-2 rounded-full bg-gate-text text-gate-bg shadow-[0_8px_12px_rgba(0,0,0,0.22)] transition-opacity hover:opacity-90 lg:bottom-10"
                >
                  <LockOpen className="h-[18px] w-[18px]" />
                  <span className="text-[13.5px] font-bold">
                    {t('gate.unlock', 'Unlock {n} more').replace(
                      '{n}',
                      moreCount != null ? groupCount(moreCount) : t('gate.thousands', 'thousands')
                    )}
                  </span>
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* ── FEATURE STORY ────────────────────────────────────────── */}
        <div className="px-[22px] pb-1.5 pt-20 lg:pt-32">
          <SectionEyebrow icon={Sparkles} text={t('gate.story.eyebrow', 'Why renters love Roostr')} />
          <h2 className="mt-1.5 text-[24px] font-semibold leading-8 text-gate-text lg:text-[32px] lg:leading-10">
            {t('gate.story.title1', 'Everything, ')}
            <span className="font-serif italic font-normal text-gate-primary">
              {t('gate.story.title2', 'working for you.')}
            </span>
          </h2>
        </div>

        <div className="flex flex-col gap-3.5 px-[22px] pt-5 md:grid md:grid-cols-2 md:gap-5 lg:pt-8">
          <FeatureCard
            eyebrow={t('gate.features.alerts.eyebrow', 'Instant alerts')}
            icon={BellRing}
            tone="primary"
            title={t('gate.features.alerts.title', 'The moment a match goes live')}
            body={t(
              'gate.features.alerts.body',
              'New flats vanish fast. Save a search once and Roostr watches the market 24/7 — pinging you the second something fits, before anyone else looks.'
            )}
            visual={<AlertVisual />}
          />
          <FeatureCard
            eyebrow={t('gate.features.ai.eyebrow', 'AI search')}
            icon={Sparkles}
            title={t('gate.features.ai.title', "Search like you'd text a friend")}
            body={t(
              'gate.features.ai.body',
              '“2+kk with a balcony, in Holešovice, under 25k total including bills.” Roostr understands price, rooms, location and amenities all at once — no fiddly filters.'
            )}
            visual={<AISearchWidget />}
          />
          <FeatureCard
            eyebrow={t('gate.features.swipe.eyebrow', 'Swipe to triage')}
            icon={Layers}
            title={t('gate.features.swipe.title', 'Only see what truly fits')}
            body={t(
              'gate.features.swipe.body',
              'New matches arrive as a tidy deck. Swipe right to keep, left to skip — a minute a day and your shortlist builds itself.'
            )}
            visual={<SwipeVisual />}
          />
          <FeatureCard
            eyebrow={t('gate.features.breakdown.eyebrow', 'Price breakdown')}
            icon={ReceiptText}
            title={t('gate.features.breakdown.title', 'The real monthly cost, itemised')}
            body={t(
              'gate.features.breakdown.body',
              "No surprises at signing. Roostr reads each listing's description and pulls out rent, utilities and deposit — so you see the true all-in cost without digging through the fine print."
            )}
            visual={<BreakdownVisual />}
          />
        </div>

        {/* ── HONEST WHY ───────────────────────────────────────────── */}
        <div className="px-[22px] pt-20 lg:pt-28">
          <div className="overflow-hidden rounded-3xl border border-gate-outline-variant bg-gate-surface-low p-5 lg:p-9">
            <div className="mb-3.5 flex h-11 w-11 items-center justify-center rounded-[14px] bg-gate-primary-container">
              <ShieldCheck className="h-6 w-6 text-gate-on-primary-container" />
            </div>
            <h3 className="mb-2 text-[18px] font-semibold text-gate-text lg:text-[22px]">
              {t('gate.why.title', 'Why sign in first?')}
            </h3>
            <p className="text-[14px] leading-[22px] text-gate-text-secondary">
              {t('gate.why.body1', "Honestly? Roostr's AI reads ")}
              <strong className="font-bold text-gate-text">
                {t('gate.why.strong1', 'thousands of listings')}
              </strong>
              {t(
                'gate.why.body2',
                " for every search — that's genuinely expensive to run. An account keeps AI search "
              )}
              <strong className="font-bold text-gate-text">
                {t('gate.why.strong2', 'fast and abuse-free')}
              </strong>
              {t(
                'gate.why.body3',
                ', and unlocks the personal stuff: saved searches, instant alerts and your shortlist, synced across every device.'
              )}
            </p>
            <div className="mt-4 flex flex-wrap gap-4">
              {(
                [
                  [Shield, t('gate.why.chip1', 'Bot-free AI search')],
                  [Bookmark, t('gate.why.chip2', 'Saved searches')],
                  [Bell, t('gate.why.chip3', 'Instant alerts')],
                ] as [React.ElementType, string][]
              ).map(([Icon, label]) => (
                <div key={label} className="flex items-center gap-[7px]">
                  <Icon className="h-[18px] w-[18px] text-gate-primary" />
                  <span className="text-[13px] font-medium text-gate-text-secondary">{label}</span>
                </div>
              ))}
            </div>

            {/* Closing CTA — relocated here from under the hero. Left-aligned
                (not centered like the old strip) to sit on the card's own text
                rail; the `gate.ctaLead` line is dropped, the card's heading and
                body already are the lead. */}
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2.5 border-t border-gate-outline-variant pt-5">
              <button
                onClick={onGoogle}
                disabled={googleBusy}
                className="flex h-10 items-center gap-2 rounded-full border border-gate-outline-variant bg-gate-surface-lowest px-4 text-[14px] font-medium text-gate-text transition-colors hover:border-gate-outline disabled:opacity-60"
              >
                <GoogleLogo />
                {t('gate.google', 'Continue with Google')}
              </button>
              <button onClick={() => signIn('sign-up')} className="text-[14px] font-bold text-gate-primary">
                {t('gate.create', 'Create your account')}
              </button>
              <span className="text-[14px] text-gate-text-secondary">
                {t('gate.already', 'Already with us?')}{' '}
                <button onClick={() => signIn('sign-in')} className="font-bold text-gate-primary">
                  {t('gate.signIn', 'Sign in')}
                </button>
              </span>
            </div>
          </div>
        </div>

        {/* Footer reassurance */}
        <p className="px-8 pb-2 pt-14 text-center text-[13px] leading-[21px] text-gate-text-muted lg:pt-16">
          {t('gate.footer', 'Bot-free AI · No spam · Made in Prague')}
        </p>
      </div>

      {/* ── STICKY BOTTOM BAR (small viewports only) ─────────────── */}
      <div className="fixed inset-x-0 bottom-0 z-40 px-4 pb-4 pt-3 sm:hidden">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, transparent, rgb(var(--gate-bg)) 40%)',
          }}
        />
        <div className="relative rounded-full bg-gate-surface-lowest shadow-[0_8px_12px_rgba(0,0,0,0.12)]">
          <GoogleButton onClick={onGoogle} busy={googleBusy} label={t('gate.google', 'Continue with Google')} />
        </div>
      </div>
    </div>
  )
}

// ── Bits ───────────────────────────────────────────────────────────

function SectionEyebrow({ icon: Icon, text }: { icon: React.ElementType; text: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="h-4 w-4 text-gate-primary" />
      <span className="text-[13px] font-medium uppercase tracking-[0.5px] text-gate-primary">
        {text}
      </span>
    </div>
  )
}

function GoogleButton({
  onClick,
  busy,
  label,
}: {
  onClick: () => void
  busy: boolean
  label: string
}) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className="flex h-[54px] w-full items-center justify-center gap-2.5 rounded-full border border-gate-outline bg-gate-surface-lowest text-[16px] font-semibold text-gate-text transition-opacity hover:opacity-90 disabled:opacity-60"
    >
      <GoogleLogo />
      {busy ? '…' : label}
    </button>
  )
}

// Same mark the AuthDialog uses.
function GoogleLogo() {
  return (
    <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  )
}

function FeatureCard({
  eyebrow,
  icon: Icon,
  tone,
  title,
  body,
  visual,
}: {
  eyebrow: string
  icon: React.ElementType
  tone?: 'primary'
  title: string
  body: string
  visual: React.ReactNode
}) {
  const isPrimary = tone === 'primary'
  return (
    <div
      className={
        'overflow-hidden rounded-3xl p-[18px] ' +
        (isPrimary
          ? 'border border-transparent bg-gate-primary-container'
          : 'border border-gate-outline-variant bg-gate-surface-low')
      }
    >
      <div className="mb-2.5 flex items-center gap-2">
        <div
          className={
            'flex h-8 w-8 items-center justify-center rounded-[10px] ' +
            (isPrimary ? 'bg-[rgba(91,15,10,0.12)]' : 'bg-gate-primary-container')
          }
        >
          <Icon className="h-[18px] w-[18px] text-gate-on-primary-container" />
        </div>
        <span
          className={
            'text-[13px] font-medium uppercase tracking-[0.5px] ' +
            (isPrimary ? 'text-gate-on-primary-container' : 'text-gate-primary')
          }
        >
          {eyebrow}
        </span>
      </div>
      <h3
        className={
          'mb-[7px] text-[18px] font-semibold leading-[25px] ' +
          (isPrimary ? 'text-gate-on-primary-container' : 'text-gate-text')
        }
      >
        {title}
      </h3>
      <p
        className={
          'mb-3.5 text-[14px] leading-[21px] ' +
          (isPrimary ? 'text-gate-on-primary-container' : 'text-gate-text-secondary')
        }
      >
        {body}
      </p>
      {visual}
    </div>
  )
}

// ── Feature visuals (small, on-brand mocks — fixed colors like mobile) ──

function AlertVisual() {
  const { t } = useI18n()
  return (
    <div className="flex items-center gap-[11px] rounded-2xl border border-[rgba(91,15,10,0.1)] bg-white/60 p-3">
      <div className="relative flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] bg-gate-primary">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/logo.svg" alt="" className="h-[26px] w-[26px] brightness-0 invert" />
        <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-[#34D399]" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-semibold text-[#1A1A1A]">
          {t('gate.visual.alertTitle', 'New match — The Quiet One')}
        </p>
        <p className="truncate text-[13px] text-[#6B6B6B]">
          {t('gate.visual.alertBody', 'Bright 2+kk near Riegrovy sady · 23 500 Kč')}
        </p>
      </div>
      <span className="text-[11px] text-[#9CA3AF]">{t('gate.visual.now', 'now')}</span>
    </div>
  )
}

// A real AI-search box (§25.12 — guests search freely now; the §25.11 auth-gate
// was removed after the meeting decision to let people try the service without
// login). Type anything → /search?q=… runs the query anonymously (rate-limited),
// unless REQUIRE_AUTH_FOR_SEARCH is on, which reinstates the sign-in gate.
// Empty submit just focuses the input.
function AISearchWidget() {
  const { t } = useI18n()
  const router = useRouter()
  const { user, signIn } = useAuth()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [value, setValue] = React.useState('')
  const [focused, setFocused] = React.useState(false)

  // Same rotating examples as the search box up top; the typewriter pauses the
  // moment the user engages so it never fights their own text.
  const placeholder = useTypewriter(EXAMPLE_QUERIES, {
    typingSpeed: 80,
    deletingSpeed: 40,
    pauseDuration: 2500,
    paused: focused || value.length > 0,
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const q = value.trim()
    if (!q) {
      inputRef.current?.focus()
      return
    }
    if (REQUIRE_AUTH_FOR_SEARCH && !user) {
      signIn()
      return
    }
    router.push(`/search?q=${encodeURIComponent(q)}`)
  }

  return (
    <form
      onSubmit={submit}
      onClick={() => inputRef.current?.focus()}
      className="flex cursor-text items-center gap-2.5 rounded-full border border-gate-outline bg-gate-surface-lowest px-3 py-2.5 transition-colors focus-within:border-gate-primary/60"
    >
      <div className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[9px] bg-gradient-to-br from-gate-primary to-[#F97316]">
        <Sparkles className="h-4 w-4 text-white" />
      </div>
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        aria-label={t('gate.features.ai.eyebrow', 'AI search')}
        className="min-w-0 flex-1 bg-transparent font-serif italic text-[15px] text-gate-text placeholder:text-gate-text-secondary focus:outline-none"
      />
      <button
        type="submit"
        aria-label={t('gate.features.ai.eyebrow', 'AI search')}
        className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-gate-primary transition-opacity hover:opacity-90"
      >
        <ArrowUp className="h-[17px] w-[17px] text-white" />
      </button>
    </form>
  )
}

function SwipeVisual() {
  const { t } = useI18n()
  const cards = [2, 1, 0] // back → front
  return (
    <div className="relative h-[84px]">
      {cards.map((d) => (
        <div
          key={d}
          className="absolute left-1/2 ml-[-84px] flex w-[168px] items-center gap-2 overflow-hidden rounded-[14px] border border-gate-outline-variant bg-gate-surface-lowest p-2 shadow-[0_6px_10px_rgba(0,0,0,0.1)]"
          style={{
            top: 10 + d * 5,
            height: 64 - d * 4,
            zIndex: 3 - d,
            transform: `rotate(${d === 0 ? -5 : d === 1 ? 2 : 6}deg)`,
          }}
        >
          <div className="h-full w-12 shrink-0 rounded-[9px] bg-gradient-to-br from-[#C9B8A8] to-[#8F7B66]" />
          {d === 0 ? (
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12.5px] font-semibold text-gate-text">
                {t('gate.visual.swipeTitle', 'Cozy 2+kk, Žižkov')}
              </p>
              <p className="mt-0.5 font-mono text-[13px] font-bold text-gate-primary">21 200 Kč</p>
            </div>
          ) : null}
        </div>
      ))}
      <div className="absolute left-2 top-1/2 z-10 -mt-[15px] flex h-[30px] w-[30px] items-center justify-center rounded-full border-[1.5px] border-gate-primary bg-gate-surface">
        <X className="h-[17px] w-[17px] text-gate-primary" />
      </div>
      <div className="absolute right-2 top-1/2 z-10 -mt-[15px] flex h-[30px] w-[30px] items-center justify-center rounded-full bg-gate-primary">
        <Heart className="h-4 w-4 fill-white text-white" />
      </div>
    </div>
  )
}

function BreakdownVisual() {
  const { t } = useI18n()
  const rows: [string, string][] = [
    [t('gate.visual.baseRent', 'Base rent'), '23 500 Kč'],
    [t('gate.visual.utilities', 'Utilities & services'), '+2 800 Kč'],
  ]
  return (
    <div className="rounded-2xl border border-gate-outline-variant bg-gate-surface-lowest p-3.5">
      {rows.map(([label, value]) => (
        <div key={label} className="mb-2 flex items-baseline justify-between">
          <span className="text-[13px] text-gate-text-secondary">{label}</span>
          <span className="font-mono text-[13px] font-semibold text-gate-text">{value}</span>
        </div>
      ))}
      <div className="mb-2.5 h-px bg-gate-outline-variant" />
      <div className="flex items-baseline justify-between">
        <span className="text-[14px] font-semibold text-gate-text">
          {t('gate.visual.totalMonth', 'Total / month')}
        </span>
        <span className="font-mono text-[19px] font-bold text-gate-primary">26 300 Kč</span>
      </div>
      <div className="mt-2.5 flex items-center gap-1.5">
        <Wallet className="h-[15px] w-[15px] text-gate-text-muted" />
        <span className="text-[13px] text-gate-text-muted">
          {t('gate.visual.depositNote', '+ 47 000 Kč deposit (2 months), one-time')}
        </span>
      </div>
    </div>
  )
}
